<template>
	<div class="lob-costs-container">
		<!-- Page Header -->
		<div class="page-header d-flex align-items-center justify-content-between mb-4">
			<div>
				<h2 class="page-title">LOB Costs Analysis</h2>
				<p class="page-subtitle">Track and analyze your LOB campaign costs with detailed filtering</p>
			</div>
			<button class="btn btn-outline-primary" @click="exportCosts">
				<i class="fad fa-file-export me-2"></i>
				Export Report
			</button>
		</div>

		<!-- Municipality Search Section -->
		<div class="search-section card mb-4">
			<div class="card-body">
				<div class="row">
					<div class="col-md-12 position-relative">
						<label class="form-label fw-bold fs-6 mb-3">
							<i class="fad fa-search me-2 text-primary"></i>
							Search For Municipality From All Campaigns
						</label>
						<div class="position-relative">
							<input
								type="text"
								v-model="searchQuery"
								class="form-control form-control-lg search-input"
								placeholder="Type municipality name (e.g., City of Chicago, Town of Springfield)..."
								@focus="searchFocused = true"
								@blur="handleSearchBlur"
							/>
							<i class="fad fa-search position-absolute top-50 end-0 translate-middle-y me-3 search-icon"></i>
						</div>
						<small class="text-muted d-block mt-2">
							<i class="fas fa-info-circle me-1"></i>
							Live search across all campaigns and time periods (minimum 2 characters)
						</small>

						<!-- Search Results Dropdown -->
						<div
							v-if="searchResults.length > 0 && searchFocused && searchQuery.length >= 2"
							class="search-results-dropdown position-absolute w-100 mt-1 bg-white border rounded shadow-lg"
							style="z-index: 1000; max-height: 400px; overflow-y: auto;"
						>
							<div
								v-for="result in searchResults"
								:key="result.municipality_id"
								@mousedown="selectSearchResult(result)"
								class="search-result-item p-3 border-bottom cursor-pointer"
								style="cursor: pointer;"
							>
								<div class="d-flex justify-content-between align-items-start">
									<div class="flex-grow-1">
										<div class="fw-bold mb-2 fs-6 text-dark">{{ result.full_name || result.municipality_name }}</div>
										<div class="d-flex flex-column gap-1">
											<div class="d-flex align-items-center gap-3 small text-muted">
												<span v-if="result.state"><i class="fas fa-flag me-1"></i>State: {{ result.state }}</span>
												<span v-if="result.county"><i class="fas fa-map-marked-alt me-1"></i>County: {{ result.county }}</span>
											</div>
											<div class="d-flex align-items-center gap-3 small text-muted">
												<span><i class="fas fa-envelope me-1"></i>{{ result.campaign_count }} campaign(s)</span>
												<span><i class="fas fa-paper-plane me-1"></i>{{ result.total_sent }} sent</span>
											</div>
										</div>
									</div>
									<div class="text-end ms-3">
										<div class="fw-bold text-primary fs-5">${{ result.total_cost.toLocaleString() }}</div>
										<div class="small text-muted">Total cost</div>
									</div>
								</div>
							</div>
							<div v-if="searchLoading" class="text-center py-3">
								<div class="spinner-border spinner-border-sm text-primary"></div>
								<small class="d-block mt-2 text-muted">Searching...</small>
							</div>
						</div>

						<!-- No results message -->
						<div
							v-if="searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && searchFocused"
							class="search-results-dropdown position-absolute w-100 mt-1 bg-white border rounded shadow-sm p-3 text-center text-muted"
							style="z-index: 1000;"
						>
							<i class="fad fa-search-minus fa-2x mb-2 opacity-50"></i>
							<div>No municipalities found matching "{{ searchQuery }}"</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Filters Section -->
		<div class="filters-card card mb-4">
			<div class="card-body">
				<div class="row g-3">
					<!-- Period Filter -->
					<div class="col-md-3">
						<label class="form-label fw-semibold">Time Period</label>
						<select v-model="filters.period" class="form-select" @change="fetchCosts">
							<option value="week">This Week</option>
							<option value="month">This Month</option>
							<option value="year">This Year</option>
							<option value="custom">Custom Range</option>
						</select>
					</div>

					<!-- Custom Date Range -->
					<div v-if="filters.period === 'custom'" class="col-md-3">
						<label class="form-label fw-semibold">From Date</label>
						<input type="date" v-model="filters.from_date" class="form-control" @change="fetchCosts" />
					</div>
					<div v-if="filters.period === 'custom'" class="col-md-3">
						<label class="form-label fw-semibold">To Date</label>
						<input type="date" v-model="filters.to_date" class="form-control" @change="fetchCosts" />
					</div>

					<!-- Sender Filter -->
					<div class="col-md-3">
						<label class="form-label fw-semibold">Sender</label>
						<select v-model="filters.sender" class="form-select" @change="fetchCosts">
							<option value="">All Senders</option>
							<option v-for="sender in availableFilters.senders" :key="sender" :value="sender">{{ sender }}</option>
						</select>
					</div>

					<!-- Campaign Type Filter -->
					<div class="col-md-3">
						<label class="form-label fw-semibold">Campaign Type</label>
						<select v-model="filters.campaign_type" class="form-select" @change="fetchCosts">
							<option value="">All Types</option>
							<option value="letter">Letters</option>
							<option value="postcard">Postcards</option>
						</select>
					</div>

					<!-- Campaign Source Filter -->
					<div class="col-md-3">
						<label class="form-label fw-semibold">Campaign Source</label>
						<select v-model="filters.campaign_source" class="form-select" @change="fetchCosts">
							<option value="">All Sources</option>
							<option value="campaigns">Manual Campaigns</option>
							<option value="auto-campaigns">Auto Campaigns</option>
							<option value="sequenced-campaigns">Sequenced Campaigns</option>
						</select>
					</div>

					<!-- ClerkMinutes Filter -->
					<div class="col-md-3">
						<label class="form-label fw-semibold">ClerkMinutes Filter</label>
						<select v-model="filters.clerk_minutes" class="form-select" @change="fetchCosts">
							<option value="">All Campaigns</option>
							<option value="only">Only ClerkMinutes</option>
							<option value="exclude">Exclude ClerkMinutes</option>
						</select>
					</div>

					<!-- Specific Campaigns Filter -->
					<div class="col-md-12">
						<label class="form-label fw-semibold">Specific Campaigns (Optional)</label>
						<select v-model="filters.selected_campaigns" class="form-select" multiple size="3" @change="fetchCosts">
							<option v-for="campaign in availableFilters.campaigns" :key="campaign.id" :value="campaign.id">
								{{ campaign.name }} ({{ campaign.type }})
							</option>
						</select>
						<small class="text-muted">Hold Ctrl/Cmd to select multiple campaigns</small>
					</div>
				</div>

				<div class="row mt-3">
					<div class="col-12">
						<button class="btn btn-secondary btn-sm me-2" @click="resetFilters">
							<i class="fad fa-redo me-1"></i>
							Reset Filters
						</button>
						<button class="btn btn-primary btn-sm" @click="fetchCosts">
							<i class="fad fa-search me-1"></i>
							Apply Filters
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Loading State -->
		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
			<p class="mt-3 text-muted">Loading cost data...</p>
		</div>

		<!-- Main Content -->
		<div v-else>
			<!-- Tabs Navigation -->
			<ul class="nav nav-tabs mb-4" role="tablist">
				<li class="nav-item" role="presentation">
					<button
						class="nav-link active"
						id="overview-tab"
						data-bs-toggle="tab"
						data-bs-target="#overview"
						type="button"
						role="tab"
					>
						<i class="fad fa-chart-line me-2"></i>
						Overview & Charts
					</button>
				</li>
				<li class="nav-item" role="presentation">
					<button
						class="nav-link"
						id="municipalities-tab"
						data-bs-toggle="tab"
						data-bs-target="#municipalities"
						type="button"
						role="tab"
					>
						<i class="fad fa-map-marked-alt me-2"></i>
						Municipality Statistics
					</button>
				</li>
			</ul>

			<!-- Tab Content -->
			<div class="tab-content">
				<!-- Overview Tab -->
				<div class="tab-pane fade show active" id="overview" role="tabpanel">
					<!-- Summary Cards -->
			<div class="row g-3 mb-4">
				<!-- Total Cost Card -->
				<div class="col-md-3">
					<div class="stat-card card">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-start">
								<div>
									<h6 class="text-muted text-uppercase mb-2">Total Cost</h6>
									<h2 class="mb-0">${{ costsData.total_cost.toLocaleString() }}</h2>
								</div>
								<div class="stat-icon bg-primary">
									<i class="fad fa-dollar-sign"></i>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Total Sent Card -->
				<div class="col-md-3">
					<div class="stat-card card">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-start">
								<div>
									<h6 class="text-muted text-uppercase mb-2">Total Sent</h6>
									<h2 class="mb-0">{{ costsData.total_sent.toLocaleString() }}</h2>
								</div>
								<div class="stat-icon bg-success">
									<i class="fad fa-paper-plane"></i>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Letters Card -->
				<div class="col-md-3">
					<div class="stat-card card">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-start">
								<div>
									<h6 class="text-muted text-uppercase mb-2">Letters</h6>
									<h3 class="mb-0">{{ costsData.letters.count.toLocaleString() }}</h3>
									<small class="text-muted">${{ costsData.letters.cost.toLocaleString() }}</small>
								</div>
								<div class="stat-icon bg-info">
									<i class="fad fa-envelope"></i>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Postcards Card -->
				<div class="col-md-3">
					<div class="stat-card card">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-start">
								<div>
									<h6 class="text-muted text-uppercase mb-2">Postcards</h6>
									<h3 class="mb-0">{{ costsData.postcards.count.toLocaleString() }}</h3>
									<small class="text-muted">${{ costsData.postcards.cost.toLocaleString() }}</small>
								</div>
								<div class="stat-icon bg-warning">
									<i class="fad fa-postcard"></i>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Charts Row -->
			<div class="row g-3 mb-4">
				<!-- Cost Trend Chart -->
				<div class="col-md-8">
					<div class="card">
						<div class="card-header">
							<h5 class="card-title mb-0">Cost Trend Over Time</h5>
						</div>
						<div class="card-body">
							<canvas ref="costTrendChart" height="100"></canvas>
						</div>
					</div>
				</div>

				<!-- Cost by Type Pie Chart -->
				<div class="col-md-4">
					<div class="card">
						<div class="card-header">
							<h5 class="card-title mb-0">Cost by Type</h5>
						</div>
						<div class="card-body">
							<canvas ref="costTypeChart" height="200"></canvas>
						</div>
					</div>
				</div>
			</div>

			<!-- Cost by Sender -->
			<div class="row g-3 mb-4">
				<div class="col-12">
					<div class="card">
						<div class="card-header">
							<h5 class="card-title mb-0">Cost Breakdown by Sender</h5>
						</div>
						<div class="card-body">
							<div class="table-responsive">
								<table class="table table-hover">
									<thead>
										<tr>
											<th>Sender</th>
											<th class="text-end">Letters</th>
											<th class="text-end">Postcards</th>
											<th class="text-end">Total Sent</th>
											<th class="text-end">Total Cost</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="sender in costsData.by_sender" :key="sender.sender">
											<td class="fw-semibold">{{ sender.sender }}</td>
											<td class="text-end">{{ sender.letters.toLocaleString() }}</td>
											<td class="text-end">{{ sender.postcards.toLocaleString() }}</td>
											<td class="text-end">{{ (sender.letters + sender.postcards).toLocaleString() }}</td>
											<td class="text-end fw-bold text-primary">${{ sender.cost.toLocaleString() }}</td>
										</tr>
									</tbody>
									<tfoot>
										<tr class="table-active fw-bold">
											<td>TOTAL</td>
											<td class="text-end">{{ costsData.letters.count.toLocaleString() }}</td>
											<td class="text-end">{{ costsData.postcards.count.toLocaleString() }}</td>
											<td class="text-end">{{ costsData.total_sent.toLocaleString() }}</td>
											<td class="text-end text-primary">${{ costsData.total_cost.toLocaleString() }}</td>
										</tr>
									</tfoot>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Cost by Municipality -->
			<div class="row g-3 mb-4">
				<div class="col-12">
					<div class="card">
						<div class="card-header">
							<h5 class="card-title mb-0">Cost Breakdown by Municipality</h5>
						</div>
						<div class="card-body">
							<div v-if="loadingMunicipalities" class="text-center py-4">
								<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
								<p class="mt-2 text-muted small">Loading municipality breakdown...</p>
							</div>
							<div v-else>
								<div class="table-responsive">
									<table class="table table-hover table-sm">
										<thead>
											<tr>
												<th>Full Municipality Name</th>
												<th>City</th>
												<th>State</th>
												<th>County</th>
												<th class="text-end">Letters</th>
												<th class="text-end">Postcards</th>
												<th class="text-end">Total Sent</th>
												<th class="text-end">Total Cost</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="muni in overviewPaginatedMunicipalities"
												:key="muni.municipality_id"
												@click="showMunicipalityCampaigns(muni)"
												style="cursor: pointer;"
												class="municipality-row"
											>
												<td>
													<router-link
														:to="`/municipalities/edit/${muni.municipality_id}`"
														@click.stop
														class="text-decoration-none fw-semibold"
														target="_blank"
													>
														{{ muni.full_name || muni.municipality_name || 'Unknown Municipality' }}
													</router-link>
												</td>
												<td>{{ muni.municipality_name || 'N/A' }}</td>
												<td>{{ muni.state || 'N/A' }}</td>
												<td>{{ muni.county || 'N/A' }}</td>
												<td class="text-end">{{ muni.letters_sent }}</td>
												<td class="text-end">{{ muni.postcards_sent }}</td>
												<td class="text-end">{{ muni.total_sent }}</td>
												<td class="text-end fw-bold text-primary">${{ muni.total_cost.toLocaleString() }}</td>
											</tr>
										</tbody>
										<tfoot v-if="filteredMunicipalityBreakdown.length > 0">
											<tr class="table-active fw-bold">
												<td colspan="4">TOTAL (All Pages)</td>
												<td class="text-end">{{ municipalityBreakdownTotal.letters }}</td>
												<td class="text-end">{{ municipalityBreakdownTotal.postcards }}</td>
												<td class="text-end">{{ municipalityBreakdownTotal.total_sent }}</td>
												<td class="text-end text-primary">${{ municipalityBreakdownTotal.total_cost.toLocaleString() }}</td>
											</tr>
										</tfoot>
									</table>
									<div v-if="filteredMunicipalityBreakdown.length === 0" class="text-center py-4">
										<p class="text-muted">No municipality data available for the selected filters.</p>
									</div>
								</div>

								<!-- Debug Info (temporary) -->
								<div class="alert alert-info mt-3 small">
									<strong>Debug Info:</strong>
									Total municipalities: {{ filteredMunicipalityBreakdown.length }} |
									Items per page: {{ overviewItemsPerPage }} |
									Total pages: {{ overviewTotalPages }} |
									Current page: {{ overviewCurrentPage }}
								</div>

								<!-- Pagination Controls -->
								<div v-if="overviewTotalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
									<div class="text-muted small">
										Showing {{ overviewPaginationStart }} to {{ overviewPaginationEnd }} of {{ filteredMunicipalityBreakdown.length }} municipalities
									</div>
									<nav>
										<ul class="pagination pagination-sm mb-0">
											<li class="page-item" :class="{ disabled: overviewCurrentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changeOverviewPage(1)">
													<i class="fas fa-angle-double-left"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: overviewCurrentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changeOverviewPage(overviewCurrentPage - 1)">
													<i class="fas fa-angle-left"></i>
												</a>
											</li>
											<li
												v-for="page in overviewVisiblePages"
												:key="page"
												class="page-item"
												:class="{ active: overviewCurrentPage === page }"
											>
												<a class="page-link" href="#" @click.prevent="changeOverviewPage(page)">{{ page }}</a>
											</li>
											<li class="page-item" :class="{ disabled: overviewCurrentPage === overviewTotalPages }">
												<a class="page-link" href="#" @click.prevent="changeOverviewPage(overviewCurrentPage + 1)">
													<i class="fas fa-angle-right"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: overviewCurrentPage === overviewTotalPages }">
												<a class="page-link" href="#" @click.prevent="changeOverviewPage(overviewTotalPages)">
													<i class="fas fa-angle-double-right"></i>
												</a>
											</li>
										</ul>
									</nav>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Cost by Campaign (Detailed View) -->
			<div class="row g-3">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<h5 class="card-title mb-0">Cost Breakdown by Campaign</h5>
							<button class="btn btn-sm btn-outline-primary" @click="fetchCampaignBreakdown">
								<i class="fad fa-sync me-1"></i>
								Refresh
							</button>
						</div>
						<div class="card-body">
							<div v-if="loadingCampaigns" class="text-center py-4">
								<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
								<p class="mt-2 text-muted small">Loading campaign breakdown...</p>
							</div>
							<div v-else>
								<div class="table-responsive">
									<table class="table table-hover table-sm">
										<thead>
											<tr>
												<th>Campaign Name</th>
												<th>Source</th>
												<th>Sender</th>
												<th class="text-end">Letters</th>
												<th class="text-end">Postcards</th>
												<th class="text-end">Total</th>
												<th class="text-end">Estimated Cost</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="campaign in campaignPaginatedBreakdown" :key="campaign.campaign_id">
											<td>
												<a
													href="javascript:void(0)"
													@click="showCampaignMunicipalities(campaign)"
													class="text-decoration-none fw-semibold"
												>
													{{ campaign.campaign_name || 'Unnamed Campaign' }}
												</a>
											</td>
											<td>
												<span
													class="badge"
													:class="{
														'bg-primary': campaign.campaign_source === 'campaigns',
														'bg-success': campaign.campaign_source === 'auto-campaigns',
														'bg-info': campaign.campaign_source === 'sequenced-campaigns'
													}"
												>
													{{ getCampaignSourceLabel(campaign.campaign_source) }}
												</span>
											</td>
											<td>{{ campaign.campaign_from }}</td>
											<td class="text-end">{{ campaign.letters_sent }}</td>
											<td class="text-end">{{ campaign.postcards_sent }}</td>
											<td class="text-end">{{ campaign.total_sent }}</td>
											<td class="text-end fw-bold">${{ campaign.estimated_cost.toLocaleString() }}</td>
										</tr>
										</tbody>
										<tfoot v-if="filteredCampaignBreakdown.length > 0">
											<tr class="table-active fw-bold">
												<td colspan="5">TOTAL (All Pages)</td>
												<td class="text-end">{{ campaignBreakdownTotal.total_sent }}</td>
												<td class="text-end text-primary">${{ campaignBreakdownTotal.total_cost.toLocaleString() }}</td>
											</tr>
										</tfoot>
									</table>
									<div v-if="filteredCampaignBreakdown.length === 0" class="text-center py-4">
										<p class="text-muted">No campaign data available for the selected filters.</p>
									</div>
								</div>

								<!-- Debug Info (temporary) -->
								<div class="alert alert-info mt-3 small">
									<strong>Debug Info:</strong>
									Total campaigns: {{ filteredCampaignBreakdown.length }} |
									Items per page: {{ campaignItemsPerPage }} |
									Total pages: {{ campaignTotalPages }} |
									Current page: {{ campaignCurrentPage }}
								</div>

								<!-- Pagination Controls -->
								<div v-if="campaignTotalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
									<div class="text-muted small">
										Showing {{ campaignPaginationStart }} to {{ campaignPaginationEnd }} of {{ filteredCampaignBreakdown.length }} campaigns
									</div>
									<nav>
										<ul class="pagination pagination-sm mb-0">
											<li class="page-item" :class="{ disabled: campaignCurrentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changeCampaignPage(1)">
													<i class="fas fa-angle-double-left"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: campaignCurrentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changeCampaignPage(campaignCurrentPage - 1)">
													<i class="fas fa-angle-left"></i>
												</a>
											</li>
											<li
												v-for="page in campaignVisiblePages"
												:key="page"
												class="page-item"
												:class="{ active: campaignCurrentPage === page }"
											>
												<a class="page-link" href="#" @click.prevent="changeCampaignPage(page)">{{ page }}</a>
											</li>
											<li class="page-item" :class="{ disabled: campaignCurrentPage === campaignTotalPages }">
												<a class="page-link" href="#" @click.prevent="changeCampaignPage(campaignCurrentPage + 1)">
													<i class="fas fa-angle-right"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: campaignCurrentPage === campaignTotalPages }">
												<a class="page-link" href="#" @click.prevent="changeCampaignPage(campaignTotalPages)">
													<i class="fas fa-angle-double-right"></i>
												</a>
											</li>
										</ul>
									</nav>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
				</div> <!-- End Overview Tab -->

				<!-- Municipality Statistics Tab -->
				<div class="tab-pane fade" id="municipalities" role="tabpanel">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<h5 class="card-title mb-0">
								<i class="fad fa-map-marked-alt me-2"></i>
								All Municipalities with Campaigns
							</h5>
							<div class="text-muted small">
								<span class="badge bg-primary">{{ paginatedMunicipalities.length }} of {{ sortedMunicipalities.length }} shown</span>
							</div>
						</div>
						<div class="card-body">
							<div v-if="loadingMunicipalities" class="text-center py-5">
								<div class="spinner-border text-primary" role="status"></div>
								<p class="mt-3 text-muted">Loading municipality data...</p>
							</div>
							<div v-else>
								<div class="table-responsive">
									<table class="table table-hover table-striped">
										<thead>
											<tr>
												<th @click="sortBy('full_name')" class="sortable-header">
													Full Municipality Name
													<i class="fas ms-2" :class="getSortIcon('full_name')"></i>
												</th>
												<th @click="sortBy('municipality_name')" class="sortable-header">
													City
													<i class="fas ms-2" :class="getSortIcon('municipality_name')"></i>
												</th>
												<th @click="sortBy('state')" class="sortable-header">
													State
													<i class="fas ms-2" :class="getSortIcon('state')"></i>
												</th>
												<th @click="sortBy('county')" class="sortable-header">
													County
													<i class="fas ms-2" :class="getSortIcon('county')"></i>
												</th>
												<th @click="sortBy('campaign_count')" class="sortable-header text-end">
													Total Campaigns
													<i class="fas ms-2" :class="getSortIcon('campaign_count')"></i>
												</th>
												<th @click="sortBy('letters_sent')" class="sortable-header text-end">
													Letters
													<i class="fas ms-2" :class="getSortIcon('letters_sent')"></i>
												</th>
												<th @click="sortBy('postcards_sent')" class="sortable-header text-end">
													Postcards
													<i class="fas ms-2" :class="getSortIcon('postcards_sent')"></i>
												</th>
												<th @click="sortBy('total_sent')" class="sortable-header text-end">
													Total Sent
													<i class="fas ms-2" :class="getSortIcon('total_sent')"></i>
												</th>
												<th @click="sortBy('total_cost')" class="sortable-header text-end">
													Total Cost
													<i class="fas ms-2" :class="getSortIcon('total_cost')"></i>
												</th>
												<th class="text-center">Actions</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="muni in paginatedMunicipalities" :key="muni.municipality_id">
												<td>
													<router-link
														:to="`/municipalities/edit/${muni.municipality_id}`"
														class="text-decoration-none fw-semibold"
														target="_blank"
													>
														{{ muni.full_name || muni.municipality_name || 'Unknown' }}
													</router-link>
												</td>
												<td>{{ muni.municipality_name || 'N/A' }}</td>
												<td>{{ muni.state || 'N/A' }}</td>
												<td>{{ muni.county || 'N/A' }}</td>
												<td class="text-end">{{ muni.campaign_count || 0 }}</td>
												<td class="text-end">{{ muni.letters_sent.toLocaleString() }}</td>
												<td class="text-end">{{ muni.postcards_sent.toLocaleString() }}</td>
												<td class="text-end">{{ muni.total_sent.toLocaleString() }}</td>
												<td class="text-end fw-bold text-primary">${{ muni.total_cost.toLocaleString() }}</td>
												<td class="text-center">
													<button
														@click="showMunicipalityCampaigns(muni)"
														class="btn btn-sm btn-outline-primary"
														title="View campaigns"
													>
														<i class="fas fa-eye"></i>
													</button>
												</td>
											</tr>
										</tbody>
										<tfoot v-if="paginatedMunicipalities.length > 0" class="table-light">
											<tr class="fw-bold">
												<td colspan="4">TOTAL (All Pages)</td>
												<td class="text-end">{{ totalCampaignCount }}</td>
												<td class="text-end">{{ totalLettersSent.toLocaleString() }}</td>
												<td class="text-end">{{ totalPostcardsSent.toLocaleString() }}</td>
												<td class="text-end">{{ totalItemsSent.toLocaleString() }}</td>
												<td class="text-end text-primary">${{ totalCost.toLocaleString() }}</td>
												<td></td>
											</tr>
										</tfoot>
									</table>
									<div v-if="sortedMunicipalities.length === 0" class="text-center py-5">
										<i class="fad fa-inbox fa-3x text-muted mb-3"></i>
										<p class="text-muted">No municipality data available for the selected filters.</p>
									</div>
								</div>

								<!-- Pagination Controls -->
								<div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-4">
									<div class="text-muted">
										Showing {{ paginationStart }} to {{ paginationEnd }} of {{ sortedMunicipalities.length }} municipalities
									</div>
									<nav>
										<ul class="pagination mb-0">
											<li class="page-item" :class="{ disabled: currentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changePage(1)">
													<i class="fas fa-angle-double-left"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: currentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
													<i class="fas fa-angle-left"></i>
												</a>
											</li>
											<li
												v-for="page in visiblePages"
												:key="page"
												class="page-item"
												:class="{ active: currentPage === page }"
											>
												<a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
											</li>
											<li class="page-item" :class="{ disabled: currentPage === totalPages }">
												<a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
													<i class="fas fa-angle-right"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: currentPage === totalPages }">
												<a class="page-link" href="#" @click.prevent="changePage(totalPages)">
													<i class="fas fa-angle-double-right"></i>
												</a>
											</li>
										</ul>
									</nav>
								</div>
							</div>
						</div>
					</div>
				</div> <!-- End Municipality Statistics Tab -->
			</div> <!-- End Tab Content -->
		</div>

		<!-- Campaign Municipalities Modal -->
		<div class="modal fade" id="campaignMunicipalitiesModal" tabindex="-1">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Municipalities in Campaign</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<div v-if="selectedCampaign">
							<h6 class="mb-3">{{ selectedCampaign.campaign_name }}</h6>
							<div v-if="loadingCampaignMunicipalities" class="text-center py-4">
								<div class="spinner-border text-primary"></div>
								<p class="mt-2">Loading municipalities...</p>
							</div>
							<div v-else>
								<div class="table-responsive">
									<table class="table table-hover">
										<thead>
											<tr>
												<th>Full Municipality Name</th>
												<th>City</th>
												<th>State</th>
												<th>County</th>
												<th class="text-end">Letters</th>
												<th class="text-end">Postcards</th>
												<th class="text-end">Total</th>
												<th class="text-end">Cost</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="muni in paginatedCampaignMunicipalities" :key="muni.municipality_id">
												<td>
													<router-link
														:to="`/municipalities/edit/${muni.municipality_id}`"
														class="text-decoration-none fw-semibold"
														target="_blank"
													>
														{{ muni.full_name || muni.municipality_name || 'Unknown' }}
													</router-link>
												</td>
												<td>{{ muni.municipality_name || 'N/A' }}</td>
												<td>{{ muni.state || 'N/A' }}</td>
												<td>{{ muni.county || 'N/A' }}</td>
												<td class="text-end">{{ muni.letters }}</td>
												<td class="text-end">{{ muni.postcards }}</td>
												<td class="text-end">{{ muni.total }}</td>
												<td class="text-end fw-bold">${{ muni.cost.toLocaleString() }}</td>
											</tr>
										</tbody>
									</table>
									<div v-if="campaignMunicipalities.length === 0" class="text-center py-4 text-muted">
										No municipality data available for this campaign.
									</div>
								</div>

								<!-- Pagination for Campaign Municipalities Modal -->
								<div v-if="campaignMunicipalitiesPages > 1" class="d-flex justify-content-between align-items-center mt-3">
									<div class="text-muted small">
										Showing {{ campaignMunicipalitiesPaginationStart }} to {{ campaignMunicipalitiesPaginationEnd }} of {{ campaignMunicipalities.length }} municipalities
									</div>
									<nav>
										<ul class="pagination pagination-sm mb-0">
											<li class="page-item" :class="{ disabled: campaignMunicipalitiesCurrentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changeCampaignMunicipalitiesPage(1)">
													<i class="fas fa-angle-double-left"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: campaignMunicipalitiesCurrentPage === 1 }">
												<a class="page-link" href="#" @click.prevent="changeCampaignMunicipalitiesPage(campaignMunicipalitiesCurrentPage - 1)">
													<i class="fas fa-angle-left"></i>
												</a>
											</li>
											<li
												v-for="page in campaignMunicipalitiesVisiblePages"
												:key="page"
												class="page-item"
												:class="{ active: campaignMunicipalitiesCurrentPage === page }"
											>
												<a class="page-link" href="#" @click.prevent="changeCampaignMunicipalitiesPage(page)">{{ page }}</a>
											</li>
											<li class="page-item" :class="{ disabled: campaignMunicipalitiesCurrentPage === campaignMunicipalitiesPages }">
												<a class="page-link" href="#" @click.prevent="changeCampaignMunicipalitiesPage(campaignMunicipalitiesCurrentPage + 1)">
													<i class="fas fa-angle-right"></i>
												</a>
											</li>
											<li class="page-item" :class="{ disabled: campaignMunicipalitiesCurrentPage === campaignMunicipalitiesPages }">
												<a class="page-link" href="#" @click.prevent="changeCampaignMunicipalitiesPage(campaignMunicipalitiesPages)">
													<i class="fas fa-angle-double-right"></i>
												</a>
											</li>
										</ul>
									</nav>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Municipality Campaigns Modal -->
		<div class="modal fade" id="municipalityCampaignsModal" tabindex="-1">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Campaigns for Municipality</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal"></button>
					</div>
					<div class="modal-body">
						<div v-if="selectedMunicipality">
							<h6 class="mb-3">{{ selectedMunicipality.municipality_name }}</h6>
							<div class="alert alert-info mb-3">
								<strong>Total Cost:</strong> ${{ selectedMunicipality.total_cost.toLocaleString() }}
							</div>
							<div v-if="loadingMunicipalityCampaigns" class="text-center py-4">
								<div class="spinner-border text-primary"></div>
								<p class="mt-2">Loading campaigns...</p>
							</div>
							<div v-else class="table-responsive">
								<table class="table table-hover">
									<thead>
										<tr>
											<th>Campaign</th>
											<th>Source</th>
											<th class="text-end">Letters</th>
											<th class="text-end">Postcards</th>
											<th class="text-end">Total</th>
											<th class="text-end">Cost</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="campaign in municipalityCampaigns" :key="campaign.campaign_id">
											<td>{{ campaign.campaign_name }}</td>
											<td>
												<span
													class="badge"
													:class="{
														'bg-primary': campaign.campaign_source === 'campaigns',
														'bg-success': campaign.campaign_source === 'auto-campaigns',
														'bg-info': campaign.campaign_source === 'sequenced-campaigns'
													}"
												>
													{{ getCampaignSourceLabel(campaign.campaign_source) }}
												</span>
											</td>
											<td class="text-end">{{ campaign.letters }}</td>
											<td class="text-end">{{ campaign.postcards }}</td>
											<td class="text-end">{{ campaign.total }}</td>
											<td class="text-end fw-bold">${{ campaign.cost.toLocaleString() }}</td>
										</tr>
									</tbody>
								</table>
								<div v-if="municipalityCampaigns.length === 0" class="text-center py-4 text-muted">
									No campaign data available for this municipality.
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Chart from 'chart.js/auto'
import restAPI from '@/api/index.js'

export default {
	name: 'LOBCosts',
	data() {
		return {
			loading: true,
			loadingCampaigns: false,
			loadingMunicipalities: false,
			loadingCampaignMunicipalities: false,
			loadingMunicipalityCampaigns: false,
			searchQuery: '',
			searchResults: [],
			searchLoading: false,
			searchFocused: false,
			filters: {
				period: 'month',
				sender: '',
				campaign_type: '',
				campaign_source: '',
				clerk_minutes: '',
				selected_campaigns: [],
				from_date: null,
				to_date: null,
			},
			costsData: {
				total_cost: 0,
				total_sent: 0,
				letters: { count: 0, cost: 0 },
				postcards: { count: 0, cost: 0 },
				by_sender: [],
				by_type: [],
				chart_data: [],
			},
			availableFilters: {
				senders: [],
				campaigns: [],
				campaign_types: ['letter', 'postcard'],
				campaign_sources: ['campaigns', 'auto-campaigns'],
			},
			campaignBreakdown: [],
			campaignBreakdownTotal: {
				total_sent: 0,
				total_cost: 0,
			},
			municipalityBreakdown: [],
			municipalityBreakdownTotal: {
				letters: 0,
				postcards: 0,
				total_sent: 0,
				total_cost: 0,
			},
			selectedCampaign: null,
			campaignMunicipalities: [],
			selectedMunicipality: null,
			municipalityCampaigns: [],
			$campaignMunicipalitiesModal: null,
			$municipalityCampaignsModal: null,
			costTrendChart: null,
			costTypeChart: null,
			// Sorting and Pagination for Municipality Statistics Tab
			sortColumn: 'full_name',
			sortDirection: 'asc',
			currentPage: 1,
			itemsPerPage: 20,
			// Pagination for Overview Municipality Breakdown Table
			overviewCurrentPage: 1,
			overviewItemsPerPage: 20,
			// Pagination for Campaign Breakdown Table
			campaignCurrentPage: 1,
			campaignItemsPerPage: 20,
			// Pagination for Campaign Municipalities Modal
			campaignMunicipalitiesCurrentPage: 1,
			campaignMunicipalitiesItemsPerPage: 20,
		}
	},
	computed: {
		filteredCampaignBreakdown() {
			return this.campaignBreakdown
		},
		filteredMunicipalityBreakdown() {
			return this.municipalityBreakdown
		},
		// Pagination computed properties for Campaign Breakdown Table
		campaignTotalPages() {
			return Math.ceil(this.filteredCampaignBreakdown.length / this.campaignItemsPerPage)
		},
		campaignPaginatedBreakdown() {
			const start = (this.campaignCurrentPage - 1) * this.campaignItemsPerPage
			const end = start + this.campaignItemsPerPage
			return this.filteredCampaignBreakdown.slice(start, end)
		},
		campaignPaginationStart() {
			return (this.campaignCurrentPage - 1) * this.campaignItemsPerPage + 1
		},
		campaignPaginationEnd() {
			const end = this.campaignCurrentPage * this.campaignItemsPerPage
			return end > this.filteredCampaignBreakdown.length ? this.filteredCampaignBreakdown.length : end
		},
		campaignVisiblePages() {
			const pages = []
			const totalPages = this.campaignTotalPages
			const currentPage = this.campaignCurrentPage

			// Show max 7 page numbers
			let startPage = Math.max(1, currentPage - 3)
			let endPage = Math.min(totalPages, startPage + 6)

			// Adjust start if we're near the end
			if (endPage - startPage < 6) {
				startPage = Math.max(1, endPage - 6)
			}

			for (let i = startPage; i <= endPage; i++) {
				pages.push(i)
			}

			return pages
		},
		// Pagination computed properties for Campaign Municipalities Modal
		campaignMunicipalitiesPages() {
			return Math.ceil(this.campaignMunicipalities.length / this.campaignMunicipalitiesItemsPerPage)
		},
		paginatedCampaignMunicipalities() {
			const start = (this.campaignMunicipalitiesCurrentPage - 1) * this.campaignMunicipalitiesItemsPerPage
			const end = start + this.campaignMunicipalitiesItemsPerPage
			return this.campaignMunicipalities.slice(start, end)
		},
		campaignMunicipalitiesPaginationStart() {
			return (this.campaignMunicipalitiesCurrentPage - 1) * this.campaignMunicipalitiesItemsPerPage + 1
		},
		campaignMunicipalitiesPaginationEnd() {
			const end = this.campaignMunicipalitiesCurrentPage * this.campaignMunicipalitiesItemsPerPage
			return end > this.campaignMunicipalities.length ? this.campaignMunicipalities.length : end
		},
		campaignMunicipalitiesVisiblePages() {
			const pages = []
			const totalPages = this.campaignMunicipalitiesPages
			const currentPage = this.campaignMunicipalitiesCurrentPage

			// Show max 7 page numbers
			let startPage = Math.max(1, currentPage - 3)
			let endPage = Math.min(totalPages, startPage + 6)

			// Adjust start if we're near the end
			if (endPage - startPage < 6) {
				startPage = Math.max(1, endPage - 6)
			}

			for (let i = startPage; i <= endPage; i++) {
				pages.push(i)
			}

			return pages
		},
		// Sorting and Pagination computed properties
		sortedMunicipalities() {
			const sorted = [...this.municipalityBreakdown].sort((a, b) => {
				let aVal = a[this.sortColumn]
				let bVal = b[this.sortColumn]

				// Special handling for full_name - fall back to municipality_name if not set
				if (this.sortColumn === 'full_name') {
					aVal = aVal || a.municipality_name || ''
					bVal = bVal || b.municipality_name || ''
				}

				// Handle null/undefined values
				if (aVal == null) aVal = ''
				if (bVal == null) bVal = ''

				// Handle string comparison (case-insensitive)
				if (typeof aVal === 'string') {
					aVal = aVal.toLowerCase()
					bVal = (typeof bVal === 'string') ? bVal.toLowerCase() : bVal
				}

				if (this.sortDirection === 'asc') {
					return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
				} else {
					return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
				}
			})

			return sorted
		},
		totalPages() {
			return Math.ceil(this.sortedMunicipalities.length / this.itemsPerPage)
		},
		paginatedMunicipalities() {
			const start = (this.currentPage - 1) * this.itemsPerPage
			const end = start + this.itemsPerPage
			return this.sortedMunicipalities.slice(start, end)
		},
		paginationStart() {
			return (this.currentPage - 1) * this.itemsPerPage + 1
		},
		paginationEnd() {
			const end = this.currentPage * this.itemsPerPage
			return end > this.sortedMunicipalities.length ? this.sortedMunicipalities.length : end
		},
		visiblePages() {
			const pages = []
			const totalPages = this.totalPages
			const currentPage = this.currentPage

			// Show max 7 page numbers
			let startPage = Math.max(1, currentPage - 3)
			let endPage = Math.min(totalPages, startPage + 6)

			// Adjust start if we're near the end
			if (endPage - startPage < 6) {
				startPage = Math.max(1, endPage - 6)
			}

			for (let i = startPage; i <= endPage; i++) {
				pages.push(i)
			}

			return pages
		},
		// Totals for all municipalities (not just current page)
		totalCampaignCount() {
			return this.sortedMunicipalities.reduce((sum, m) => sum + (m.campaign_count || 0), 0)
		},
		totalLettersSent() {
			return this.sortedMunicipalities.reduce((sum, m) => sum + (m.letters_sent || 0), 0)
		},
		totalPostcardsSent() {
			return this.sortedMunicipalities.reduce((sum, m) => sum + (m.postcards_sent || 0), 0)
		},
		totalItemsSent() {
			return this.sortedMunicipalities.reduce((sum, m) => sum + (m.total_sent || 0), 0)
		},
		totalCost() {
			return this.sortedMunicipalities.reduce((sum, m) => sum + (m.total_cost || 0), 0)
		},
		// Pagination computed properties for Overview Municipality Breakdown Table
		overviewTotalPages() {
			return Math.ceil(this.filteredMunicipalityBreakdown.length / this.overviewItemsPerPage)
		},
		overviewPaginatedMunicipalities() {
			const start = (this.overviewCurrentPage - 1) * this.overviewItemsPerPage
			const end = start + this.overviewItemsPerPage
			return this.filteredMunicipalityBreakdown.slice(start, end)
		},
		overviewPaginationStart() {
			return (this.overviewCurrentPage - 1) * this.overviewItemsPerPage + 1
		},
		overviewPaginationEnd() {
			const end = this.overviewCurrentPage * this.overviewItemsPerPage
			return end > this.filteredMunicipalityBreakdown.length ? this.filteredMunicipalityBreakdown.length : end
		},
		overviewVisiblePages() {
			const pages = []
			const totalPages = this.overviewTotalPages
			const currentPage = this.overviewCurrentPage

			// Show max 7 page numbers
			let startPage = Math.max(1, currentPage - 3)
			let endPage = Math.min(totalPages, startPage + 6)

			// Adjust start if we're near the end
			if (endPage - startPage < 6) {
				startPage = Math.max(1, endPage - 6)
			}

			for (let i = startPage; i <= endPage; i++) {
				pages.push(i)
			}

			return pages
		},
	},
	async mounted() {
		const { Modal } = await import('bootstrap')
		this.$campaignMunicipalitiesModal = new Modal('#campaignMunicipalitiesModal')
		this.$municipalityCampaignsModal = new Modal('#municipalityCampaignsModal')

		await this.fetchCosts()
		await this.fetchCampaignBreakdown()
		await this.fetchMunicipalityBreakdown()
	},
	beforeDestroy() {
		this.destroyCharts()
	},
	watch: {
		searchQuery() {
			if (!this.searchLoading) {
				this.searchMunicipalities()
			}
		},
		filters: {
			handler() {
				// Auto-refresh campaign breakdown when filters change
				this.fetchCampaignBreakdown()
				this.fetchMunicipalityBreakdown()
			},
			deep: true,
		},
	},
	methods: {
		async fetchCosts() {
			this.loading = true
			try {
				const params = {
					period: this.filters.period,
					sender: this.filters.sender,
					campaign_type: this.filters.campaign_type,
					campaign_source: this.filters.campaign_source,
					clerk_minutes: this.filters.clerk_minutes,
					selected_campaigns: this.filters.selected_campaigns,
				}

				if (this.filters.period === 'custom') {
					params.from_date = this.filters.from_date
					params.to_date = this.filters.to_date
				}

				const response = await restAPI.get('/lob/costs', { params })

				this.costsData = response.data.costs
				this.availableFilters = response.data.filters

				// Update charts
				this.$nextTick(() => {
					this.renderCostTrendChart()
					this.renderCostTypeChart()
				})
			} catch (error) {
				console.error('Error fetching costs:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Error Loading Costs',
					text: error.response?.data?.message || 'Failed to load cost data',
				})
			} finally {
				this.loading = false
			}
		},
		async fetchCampaignBreakdown() {
			this.loadingCampaigns = true
			try {
				const params = {
					period: this.filters.period,
					sender: this.filters.sender,
					campaign_type: this.filters.campaign_type,
					campaign_source: this.filters.campaign_source,
					clerk_minutes: this.filters.clerk_minutes,
				}

				if (this.filters.period === 'custom') {
					params.from_date = this.filters.from_date
					params.to_date = this.filters.to_date
				}

				const response = await restAPI.get('/lob/costs/by-campaign', { params })

				this.campaignBreakdown = response.data.campaigns
				this.campaignBreakdownTotal = {
					total_sent: response.data.total_sent,
					total_cost: response.data.total_cost,
				}
			} catch (error) {
				console.error('Error fetching campaign breakdown:', error)
			} finally {
				this.loadingCampaigns = false
			}
		},
		renderCostTrendChart() {
			if (this.costTrendChart) {
				this.costTrendChart.destroy()
			}

			const ctx = this.$refs.costTrendChart.getContext('2d')
			this.costTrendChart = new Chart(ctx, {
				type: 'line',
				data: {
					labels: this.costsData.chart_data.map(d => d.label),
					datasets: [
						{
							label: 'Total Cost',
							data: this.costsData.chart_data.map(d => d.cost),
							borderColor: '#0d6efd',
							backgroundColor: 'rgba(13, 110, 253, 0.1)',
							borderWidth: 2,
							fill: true,
							tension: 0.4,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							display: false,
						},
						tooltip: {
							callbacks: {
								label: context => `Cost: $${context.parsed.y.toLocaleString()}`,
							},
						},
					},
					scales: {
						y: {
							beginAtZero: true,
							ticks: {
								callback: value => `$${value}`,
							},
						},
					},
				},
			})
		},
		renderCostTypeChart() {
			if (this.costTypeChart) {
				this.costTypeChart.destroy()
			}

			const ctx = this.$refs.costTypeChart.getContext('2d')
			this.costTypeChart = new Chart(ctx, {
				type: 'doughnut',
				data: {
					labels: this.costsData.by_type.map(t => t.type),
					datasets: [
						{
							data: this.costsData.by_type.map(t => t.cost),
							backgroundColor: ['#0dcaf0', '#ffc107'],
							borderWidth: 2,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							position: 'bottom',
						},
						tooltip: {
							callbacks: {
								label: context => {
									const label = context.label || ''
									const value = context.parsed || 0
									const total = context.dataset.data.reduce((a, b) => a + b, 0)
									const percentage = ((value / total) * 100).toFixed(1)
									return `${label}: $${value.toLocaleString()} (${percentage}%)`
								},
							},
						},
					},
				},
			})
		},
		destroyCharts() {
			if (this.costTrendChart) {
				this.costTrendChart.destroy()
				this.costTrendChart = null
			}
			if (this.costTypeChart) {
				this.costTypeChart.destroy()
				this.costTypeChart = null
			}
		},
		resetFilters() {
			this.filters = {
				period: 'month',
				sender: '',
				campaign_type: '',
				campaign_source: '',
				clerk_minutes: '',
				selected_campaigns: [],
				from_date: null,
				to_date: null,
			}
			this.searchQuery = ''
			this.searchResults = []
			this.fetchCosts()
			this.fetchCampaignBreakdown()
			this.fetchMunicipalityBreakdown()
		},
		async searchMunicipalities() {
			if (this.searchQuery.length < 2) {
				this.searchResults = []
				return
			}

			this.searchLoading = true
			try {
				const response = await restAPI.get('/lob/costs/by-municipality', {
					params: {
						period: 'year', // Search across all time for comprehensive results
					},
				})

				// Filter and score the results by search query with smart matching
				const query = this.searchQuery.toLowerCase().trim()
				console.log('=== SEARCH DEBUG ===')
				console.log('Query:', query)
				console.log('Total municipalities:', response.data.municipalities.length)

				const municipalitiesWithScore = response.data.municipalities
					.map(muni => {
						if (!muni.municipality_name) return null

						// Use full_name for scoring if available, otherwise fall back to municipality_name
						const nameToScore = (muni.full_name || muni.municipality_name).toLowerCase()
						const score = this.calculateRelevanceScore(nameToScore, query)

						if (score === 0) return null

						// Log scoring details
						if (nameToScore.includes('adams')) {
							console.log(`Searching: ${nameToScore}, Score: ${score}`)
						}

						return {
							...muni,
							campaign_count: muni.campaign_count || 0,
							relevanceScore: score,
						}
					})
					.filter(muni => muni !== null)

				// Sort by relevance score (highest first), then by total_cost
				this.searchResults = municipalitiesWithScore.sort((a, b) => {
					if (b.relevanceScore !== a.relevanceScore) {
						return b.relevanceScore - a.relevanceScore
					}
					return b.total_cost - a.total_cost
				})

				console.log('Top 5 results:')
				this.searchResults.slice(0, 5).forEach(r => {
					console.log(`  ${r.municipality_name} (${r.full_name}) - Score: ${r.relevanceScore}`)
				})
			} catch (error) {
				console.error('Error searching municipalities:', error)
				this.searchResults = []
			} finally {
				this.searchLoading = false
			}
		},
		calculateRelevanceScore(municipalityName, query) {
			// Calculate relevance score (higher = more relevant)
			// Score 0 = no match

			const municipalityNameLower = municipalityName.toLowerCase()
			const queryLower = query.toLowerCase()

			// Check if query contains a municipality type
			const queryHasType = this.extractMunicipalityType(queryLower)
			const nameHasType = this.extractMunicipalityType(municipalityNameLower)

			// If user specified a type in query but municipality has a DIFFERENT type, exclude it
			if (queryHasType && nameHasType && queryHasType !== nameHasType) {
				return 0 // Completely exclude mismatched types
			}

			// Get the core name from the query (remove type if present)
			const normalizedQuery = this.normalizeMunicipalityName(queryLower)

			// If user typed "city adams", we want to match just "adams"
			// but with lower priority than if it had "city" in the name
			if (queryHasType && !nameHasType) {
				// Query has type but municipality name doesn't have type info
				// Just match on the core name, but with reduced priority
				const coreQuery = normalizedQuery

				// Check if municipality name matches the core part of the query
				if (municipalityNameLower === coreQuery) {
					return 300 // Lower priority since we can't verify the type
				}
				if (municipalityNameLower.startsWith(coreQuery)) {
					return 250
				}
				if (municipalityNameLower.includes(coreQuery)) {
					return 200
				}
				// No match on core name
				return 0
			}

			// SPECIAL CASE: "city adams" should strongly match "City of Adams" (if type is in name)
			if (queryHasType && nameHasType && queryHasType === nameHasType) {
				const normalizedQueryCore = this.normalizeMunicipalityName(queryLower) // gets "adams"
				const expectedFormats = [
					`${queryHasType} of ${normalizedQueryCore}`,
					`${normalizedQueryCore} ${queryHasType}`,
					`${queryHasType} ${normalizedQueryCore}`,
				]

				for (const format of expectedFormats) {
					if (municipalityNameLower === format) {
						return 2000 // SUPER HIGH score for perfect type + name match
					}
					if (municipalityNameLower.startsWith(format)) {
						return 1900
					}
				}
			}

			// Bonus for matching municipality type in query
			let typeMatchBonus = 0
			if (queryHasType && nameHasType && queryHasType === nameHasType) {
				typeMatchBonus = 500 // HUGE bonus for matching the type user specified
			}

			// 1. Exact match (case-insensitive) - highest priority
			if (municipalityNameLower === queryLower) {
				return 1000 + typeMatchBonus
			}

			// 2. Starts with exact query
			if (municipalityNameLower.startsWith(queryLower)) {
				return 900 + typeMatchBonus
			}

			// 3. Ends with exact query
			if (municipalityNameLower.endsWith(queryLower)) {
				return 850 + typeMatchBonus
			}

			// 4. Direct substring match
			if (municipalityNameLower.includes(queryLower)) {
				// Give higher score if match is at word boundary
				const words = municipalityNameLower.split(/\s+/)
				if (words.some(word => word === queryLower)) {
					return 800 + typeMatchBonus
				}
				if (words.some(word => word.startsWith(queryLower))) {
					return 750 + typeMatchBonus
				}
				return 700 + typeMatchBonus
			}

			// 5. Normalized exact match
			const normalized = this.normalizeMunicipalityName(municipalityNameLower)
			// normalizedQuery already declared above, reuse it

			if (normalized === normalizedQuery) {
				return 600 + typeMatchBonus
			}

			if (normalized.startsWith(normalizedQuery)) {
				return 550 + typeMatchBonus
			}

			if (normalized.includes(normalizedQuery)) {
				return 500 + typeMatchBonus
			}

			// 6. Variation matching (least priority)
			const variations = this.generateMunicipalityVariations(queryLower)
			for (let i = 0; i < variations.length; i++) {
				const variation = variations[i]
				if (municipalityNameLower === variation) {
					return 400 - i + typeMatchBonus
				}
				if (municipalityNameLower.includes(variation)) {
					return 300 - i + typeMatchBonus
				}
				const normalizedVariation = this.normalizeMunicipalityName(variation)
				if (normalized.includes(normalizedVariation)) {
					return 200 - i + typeMatchBonus
				}
			}

			// No match
			return 0
		},
		extractMunicipalityType(name) {
			// Extract municipality type from name (city, town, village, etc.)
			const nameLower = name.toLowerCase().trim()
			// Order matters - check longer variants first to avoid false matches
			const types = ['township', 'borough', 'village', 'city', 'town']

			for (const type of types) {
				// Check if it starts with "type of " or "type " (with space after)
				if (nameLower.startsWith(`${type} of `)) {
					return type
				}
				if (nameLower.startsWith(`${type} `) && !nameLower.startsWith(`${type} of `)) {
					return type
				}
				// Check if it ends with " type" (with space before)
				if (nameLower.endsWith(` ${type}`)) {
					return type
				}
				// Check if the name is just the type and another word (e.g., "city adams")
				const words = nameLower.split(/\s+/)
				if (words.length === 2 && words[0] === type) {
					return type
				}
			}

			return null
		},
		normalizeMunicipalityName(name) {
			// Remove common prefixes and suffixes to get core municipality name
			const prefixes = [
				'town of', 'city of', 'village of', 'borough of', 'township of',
				'town', 'city', 'village', 'borough', 'township', 'county of', 'county'
			]
			const suffixes = [
				'town', 'city', 'village', 'borough', 'township', 'county'
			]

			let normalized = name.toLowerCase().trim()

			// Remove prefixes
			for (const prefix of prefixes) {
				if (normalized.startsWith(prefix + ' ')) {
					normalized = normalized.substring(prefix.length + 1).trim()
					break
				}
			}

			// Remove suffixes
			for (const suffix of suffixes) {
				if (normalized.endsWith(' ' + suffix)) {
					normalized = normalized.substring(0, normalized.length - suffix.length - 1).trim()
					break
				}
			}

			return normalized
		},
		generateMunicipalityVariations(query) {
			// Generate variations of the query with different prefixes/suffixes
			const variations = [query]
			const prefixes = ['town of', 'city of', 'village of', 'borough of', 'township of']
			const suffixes = ['town', 'city', 'village', 'borough', 'township']

			// Add prefix variations
			for (const prefix of prefixes) {
				variations.push(`${prefix} ${query}`)
			}

			// Add suffix variations
			for (const suffix of suffixes) {
				variations.push(`${query} ${suffix}`)
			}

			return variations
		},
		selectSearchResult(result) {
			this.searchFocused = false
			this.searchQuery = ''
			this.searchResults = []
			// Show the municipality campaigns modal with all campaigns across all time
			this.showMunicipalityCampaigns(result, true)
		},
		handleSearchBlur() {
			// Delay hiding to allow click events to fire
			setTimeout(() => {
				this.searchFocused = false
			}, 200)
		},
		async fetchMunicipalityBreakdown() {
			this.loadingMunicipalities = true
			try {
				const params = {
					period: this.filters.period,
					sender: this.filters.sender,
					campaign_type: this.filters.campaign_type,
					campaign_source: this.filters.campaign_source,
					clerk_minutes: this.filters.clerk_minutes,
				}

				if (this.filters.period === 'custom') {
					params.from_date = this.filters.from_date
					params.to_date = this.filters.to_date
				}

				const response = await restAPI.get('/lob/costs/by-municipality', { params })

				this.municipalityBreakdown = response.data.municipalities
				this.municipalityBreakdownTotal = {
					letters: response.data.total_letters || 0,
					postcards: response.data.total_postcards || 0,
					total_sent: response.data.total_sent,
					total_cost: response.data.total_cost,
				}
			} catch (error) {
				console.error('Error fetching municipality breakdown:', error)
			} finally {
				this.loadingMunicipalities = false
			}
		},
		async showCampaignMunicipalities(campaign) {
			this.selectedCampaign = campaign
			this.campaignMunicipalities = []
			this.campaignMunicipalitiesCurrentPage = 1 // Reset to first page
			this.loadingCampaignMunicipalities = true
			this.$campaignMunicipalitiesModal.show()

			try {
				const response = await restAPI.get(`/lob/costs/campaign/${campaign.campaign_id}/municipalities`, {
					params: {
						campaign_source: campaign.campaign_source,
					},
				})
				this.campaignMunicipalities = response.data.municipalities
			} catch (error) {
				console.error('Error fetching campaign municipalities:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Failed to load municipalities for this campaign',
				})
			} finally {
				this.loadingCampaignMunicipalities = false
			}
		},
		async showMunicipalityCampaigns(municipality, useAllTime = false) {
			this.selectedMunicipality = municipality
			this.municipalityCampaigns = []
			this.loadingMunicipalityCampaigns = true
			this.$municipalityCampaignsModal.show()

			try {
				const params = {}

				if (useAllTime) {
					// When clicked from search, show ALL campaigns across all time
					params.period = 'year'
				} else {
					// When clicked from table, use current filters
					params.period = this.filters.period
					params.sender = this.filters.sender
					params.campaign_type = this.filters.campaign_type
					params.campaign_source = this.filters.campaign_source

					if (this.filters.period === 'custom') {
						params.from_date = this.filters.from_date
						params.to_date = this.filters.to_date
					}
				}

				const response = await restAPI.get(`/lob/costs/municipality/${municipality.municipality_id}/campaigns`, {
					params,
				})
				this.municipalityCampaigns = response.data.campaigns
			} catch (error) {
				console.error('Error fetching municipality campaigns:', error)
				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: 'Failed to load campaigns for this municipality',
				})
			} finally {
				this.loadingMunicipalityCampaigns = false
			}
		},
		getCampaignSourceLabel(source) {
			const labels = {
				campaigns: 'Manual',
				'auto-campaigns': 'Auto',
				'sequenced-campaigns': 'Sequenced',
			}
			return labels[source] || source || 'N/A'
		},
		async exportCosts() {
			this.$swal.fire({
				icon: 'info',
				title: 'Export Feature',
				text: 'Export functionality will be implemented in the next phase.',
				confirmButtonText: 'OK',
			})
		},
		// Sorting methods
		sortBy(column) {
			if (this.sortColumn === column) {
				// Toggle direction if clicking same column
				this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
			} else {
				// Set new column and default to descending for numeric columns, ascending for text
				this.sortColumn = column
				const numericColumns = ['campaign_count', 'letters_sent', 'postcards_sent', 'total_sent', 'total_cost']
				const textColumns = ['full_name', 'municipality_name', 'state', 'county']
				if (numericColumns.includes(column)) {
					this.sortDirection = 'desc'
				} else if (textColumns.includes(column)) {
					this.sortDirection = 'asc'
				} else {
					this.sortDirection = 'asc'
				}
			}
			// Reset to first page when sorting changes
			this.currentPage = 1
		},
		getSortIcon(column) {
			if (this.sortColumn !== column) {
				return 'fa-sort text-muted'
			}
			return this.sortDirection === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary'
		},
		// Pagination methods
		changePage(page) {
			if (page < 1 || page > this.totalPages) return
			this.currentPage = page
			// Scroll to top of table when changing pages
			window.scrollTo({ top: 0, behavior: 'smooth' })
		},
		changeOverviewPage(page) {
			if (page < 1 || page > this.overviewTotalPages) return
			this.overviewCurrentPage = page
		},
		changeCampaignPage(page) {
			if (page < 1 || page > this.campaignTotalPages) return
			this.campaignCurrentPage = page
		},
		changeCampaignMunicipalitiesPage(page) {
			if (page < 1 || page > this.campaignMunicipalitiesPages) return
			this.campaignMunicipalitiesCurrentPage = page
		},
	},
}
</script>

<style scoped>
.lob-costs-container {
	padding: 1.5rem;
}

.page-header {
	margin-bottom: 2rem;
}

.page-title {
	font-size: 28px;
	font-weight: 600;
	color: #1f2937;
	margin: 0;
}

.page-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0.5rem 0 0 0;
}

.search-section {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	border: none;
	border-radius: 12px;
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.search-section .card-body {
	padding: 1.5rem;
}

.search-section label {
	color: white;
	font-size: 18px;
	margin-bottom: 1rem;
}

.search-section label .text-primary {
	color: white !important;
}

.search-section .text-muted {
	color: rgba(255, 255, 255, 0.8) !important;
}

.search-icon {
	color: #9ca3af;
	pointer-events: none;
}

.search-input {
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-radius: 8px;
	padding: 0.75rem 3rem 0.75rem 1rem;
	font-size: 16px;
	background: white;
	transition: all 0.3s ease;
}

.search-input:focus {
	border-color: white;
	box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
	outline: none;
}

.search-input::placeholder {
	color: #9ca3af;
}

.filters-card {
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-card {
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-icon {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 24px;
}

.card {
	border: 1px solid #e5e7eb;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-header {
	background-color: #f9fafb;
	border-bottom: 1px solid #e5e7eb;
	padding: 1rem 1.5rem;
}

.card-title {
	font-size: 16px;
	font-weight: 600;
	color: #1f2937;
}

.table {
	margin-bottom: 0;
}

.table th {
	font-weight: 600;
	color: #374151;
	text-transform: uppercase;
	font-size: 12px;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e5e7eb;
}

.table tbody tr {
	transition: background-color 0.2s ease;
}

.table tbody tr:hover {
	background-color: #f9fafb;
}

.badge {
	font-size: 11px;
	padding: 4px 8px;
	font-weight: 600;
}

.municipality-row:hover {
	background-color: #f0f9ff !important;
	cursor: pointer;
}

.search-results-dropdown {
	max-height: 300px;
	overflow-y: auto;
	background-color: white !important;
}

.search-result-item {
	transition: background-color 0.2s ease;
	background-color: white !important;
}

.search-result-item:hover {
	background-color: #f8f9fa !important;
}

.search-result-item .text-dark {
	color: #1f2937 !important;
}

.search-result-item .text-muted {
	color: #6b7280 !important;
}

.search-result-item .text-primary {
	color: #0d6efd !important;
}

/* Sortable table headers */
.sortable-header {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s ease;
}

.sortable-header:hover {
	background-color: #e9ecef !important;
}

.sortable-header i {
	font-size: 12px;
	opacity: 0.5;
	transition: opacity 0.2s ease;
}

.sortable-header:hover i {
	opacity: 1;
}

.sortable-header .text-primary {
	opacity: 1 !important;
}

/* Pagination styling */
.pagination .page-link {
	color: #667eea;
	border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
	background-color: #667eea;
	border-color: #667eea;
}

.pagination .page-link:hover {
	background-color: #f8f9fa;
	color: #764ba2;
}

.pagination .page-item.disabled .page-link {
	color: #6c757d;
	cursor: not-allowed;
}

/* Tab styling */
.nav-tabs .nav-link {
	color: #6b7280;
	font-weight: 500;
	border: none;
	border-bottom: 3px solid transparent;
	transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
	color: #667eea;
	border-bottom-color: rgba(102, 126, 234, 0.3);
}

.nav-tabs .nav-link.active {
	color: #667eea;
	border-bottom-color: #667eea;
	background: transparent;
}

.tab-content {
	animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
